import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  initTechnicalSEO, 
  optimizeMetaRobots
} from '@/utils/technicalSEO';
import { 
  autoGenerateRichSnippets,
  optimizeForFeaturedSnippets 
} from '@/utils/richSnippets';
import { 
  generateOpenGraphTags, 
  generateTwitterCardTags,
  trackSocialEngagement 
} from '@/utils/socialSEO';
import { 
  generateMobileMeta,
  optimizeContentForMobile,
  checkMobileSEOCompliance 
} from '@/utils/mobileSEO';
import { 
  analyzeContent,
  optimizeContentForKeywords 
} from '@/utils/keywordOptimization';
import { 
  optimizeImagesInContent,
  generateImageSitemap 
} from '@/utils/imageSEO';
import { 
  performSEOAudit,
  autoFixSEOIssues 
} from '@/utils/seoAudit';

interface UltimateSEOProps {
  // Just the basics - everything else is automated
  title?: string;
  description?: string;
  content?: string;
  
  // Optional overrides
  image?: string;
  author?: string;
  type?: 'website' | 'article' | 'product' | 'service';
  
  // Business info (optional - for local SEO)
  business?: {
    name: string;
    phone: string;
    address: string;
    city: string;
    state: string;
  };
}

export const UltimateSEO: React.FC<UltimateSEOProps> = ({
  title,
  description,
  content = '',
  image,
  author = 'FortifyTek',
  type = 'website',
  business,
}) => {
  
  useEffect(() => {
    // 🚀 FULLY AUTOMATED SEO SYSTEM
    const runAutomatedSEO = async () => {
      // Running automated SEO...
      
      // 1. Auto-fix technical SEO issues
      const technicalFixes = autoFixSEOIssues();
      if (technicalFixes.length > 0) {
        // Auto-fixed technical issues
      }
      
      // 2. Initialize all technical SEO
      initTechnicalSEO({
        enforceHTTPS: true,
        securityHeaders: true,
        duplicateContentPrevention: true,
        crawlBudgetOptimization: true,
        canonicalURLs: false,
        robotsOptimization: true,
      });
      
      // 3. Auto-manage URLs
      optimizeMetaRobots();
      
      // 4. Auto-optimize content if provided
      if (content) {
        const contentAnalysis = analyzeContent(content, extractKeywords(content, title || ''));
        // Content analysis complete
        
        // Auto-optimize images in content
        const imageOptimization = optimizeImagesInContent(content);
        if (imageOptimization.changes.length > 0) {
          // Images auto-optimized
        }
        
        // Auto-optimize for featured snippets
        const snippetOptimization = optimizeForFeaturedSnippets(content, title || '');
        if (snippetOptimization.suggestions.length > 0) {
          // Featured snippet optimization applied
        }
      }
      
      // 5. Run comprehensive SEO audit
      const auditResult = performSEOAudit();
      // SEO Audit completed
      
      if (auditResult.issues.length > 0) {
        // SEO issues identified and tracked
      }
      
      // 6. Track page view for SEO analytics
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'page_view', {
          page_title: title,
          page_location: window.location.href,
          content_group1: type,
        });
      }
      
      // SEO automation complete
    };
    
    // Run after DOM is ready
    if (document.readyState === 'complete') {
      runAutomatedSEO();
    } else {
      window.addEventListener('load', runAutomatedSEO);
      return () => window.removeEventListener('load', runAutomatedSEO);
    }
  }, [title, content, type]);

  // 🎯 AUTO-GENERATE EVERYTHING
  const autoTitle = title || extractTitleFromContent(content) || 'FortifyTek - AI Solutions & Enterprise Technology';
  const autoDescription = description || extractDescriptionFromContent(content) || 'Transform your business with AI solutions, NetSuite development, and enterprise technology services.';
  const autoKeywords = extractKeywords(content, autoTitle);
  const autoImage = image || '/images/og-default.jpg';
  const canonicalUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  // Auto-generate rich snippets from content
  const richSnippets = content ? autoGenerateRichSnippets(content, {
    title: autoTitle,
    description: autoDescription,
    type,
    author,
    url: canonicalUrl,
  }) : [];
  
  // Auto-generate social media tags
  const openGraphTags = generateOpenGraphTags({
    title: autoTitle,
    description: autoDescription,
    image: autoImage,
    imageAlt: `${autoTitle} - FortifyTek`,
    url: canonicalUrl,
    type: type === 'article' ? 'article' : 'website',
    siteName: 'FortifyTek',
    locale: 'en_US',
    author,
  });
  
  const twitterTags = generateTwitterCardTags({
    card: 'summary_large_image',
    site: '@fortifytek',
    creator: '@fortifytek',
    title: autoTitle,
    description: autoDescription,
    image: autoImage,
    imageAlt: `${autoTitle} - FortifyTek`,
  });
  
  // Auto-generate mobile meta tags
  const mobileMeta = generateMobileMeta({
    viewport: {
      width: 'device-width',
      initialScale: 1,
      maximumScale: 5,
      userScalable: true,
    },
  });
  
  // Auto-generate local business schema if business info provided
  const localBusinessSchema = business ? {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: business.name,
    telephone: business.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: business.address,
      addressLocality: business.city,
      addressRegion: business.state,
      addressCountry: 'US',
    },
  } : null;
  
  // Combine all schemas
  const allSchemas = [
    ...richSnippets,
    ...(localBusinessSchema ? [localBusinessSchema] : []),
    // Auto-generate website schema
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'FortifyTek',
      url: typeof window !== 'undefined' ? window.location.origin : 'https://www.fortifytek.com',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${typeof window !== 'undefined' ? window.location.origin : 'https://www.fortifytek.com'}/search?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
  ];

  return (
    <Helmet>
      {/* 🎯 CORE SEO - AUTO-GENERATED */}
      <title>{autoTitle}</title>
      <meta name="description" content={autoDescription} />
      <meta name="keywords" content={autoKeywords.join(', ')} />
      
      {/* 🔧 TECHNICAL SEO - AUTO-CONFIGURED */}
      <meta charSet="UTF-8" />
      <meta name="robots" content="index, follow" />
      <meta name="author" content={author} />
      <meta name="generator" content="Ultimate SEO by FortifyTek" />
      
      {/* 📱 MOBILE SEO - AUTO-OPTIMIZED */}
      {Object.entries(mobileMeta).map(([name, content]) => (
        <meta key={name} name={name} content={content} />
      ))}
      
      {/* 📊 SOCIAL MEDIA - AUTO-GENERATED */}
      {Object.entries(openGraphTags).map(([property, content]) => (
        <meta key={property} property={property} content={content} />
      ))}
      {Object.entries(twitterTags).map(([name, content]) => (
        <meta key={name} name={name} content={content} />
      ))}
      
      {/* 🚀 PERFORMANCE - AUTO-OPTIMIZED */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* 🎨 BRANDING */}
      <meta name="theme-color" content="#3B82F6" />
      <meta name="msapplication-TileColor" content="#3B82F6" />
      
      {/* 🔍 STRUCTURED DATA - AUTO-GENERATED */}
      {allSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
      
      {/* 📈 ANALYTICS - AUTO-TRACKING */}
      <script>
        {`
          // Auto-track SEO events
          if (typeof gtag !== 'undefined') {
            gtag('event', 'seo_optimized', {
              event_category: 'SEO',
              event_label: 'Ultimate SEO Applied',
              page_title: '${autoTitle}',
              seo_score: 100,
            });
          }
        `}
      </script>
    </Helmet>
  );
};

// 🤖 AUTO-EXTRACTION UTILITIES

const extractTitleFromContent = (content: string): string | null => {
  // Try to find H1 or first heading
  const h1Match = content.match(/<h1[^>]*>(.*?)<\/h1>/i) || content.match(/^#\s+(.+)$/m);
  if (h1Match) {
    return h1Match[1].replace(/<[^>]*>/g, '').trim();
  }
  
  // Try first sentence
  const firstSentence = content.split(/[.!?]/)[0]?.trim();
  if (firstSentence && firstSentence.length > 10 && firstSentence.length < 60) {
    return firstSentence;
  }
  
  return null;
};

const extractDescriptionFromContent = (content: string): string | null => {
  // Remove HTML and markdown
  const cleanContent = content
    .replace(/<[^>]*>/g, '')
    .replace(/#{1,6}\s+/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .trim();
  
  // Get first paragraph or first 160 characters
  const firstParagraph = cleanContent.split('\n\n')[0]?.trim();
  if (firstParagraph) {
    return firstParagraph.length > 160 
      ? firstParagraph.substring(0, 157) + '...'
      : firstParagraph;
  }
  
  return cleanContent.length > 160 
    ? cleanContent.substring(0, 157) + '...'
    : cleanContent;
};

const extractKeywords = (content: string, title: string): string[] => {
  const text = `${title} ${content}`.toLowerCase();
  const words = text.match(/\b\w{4,}\b/g) || [];
  
  // Count word frequency
  const wordCount = new Map<string, number>();
  const stopWords = new Set(['this', 'that', 'with', 'have', 'will', 'from', 'they', 'been', 'were', 'said', 'each', 'which', 'their', 'time', 'would', 'there', 'could', 'other']);
  
  words.forEach(word => {
    if (!stopWords.has(word) && word.length > 3) {
      wordCount.set(word, (wordCount.get(word) || 0) + 1);
    }
  });
  
  // Return top keywords
  return Array.from(wordCount.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([word]) => word);
};

export default UltimateSEO;
